// src/component/utils/auth.utils.ts

interface SessionData {
  admin_id: number;
  email: string;
  login_time: number;
  remember_device: boolean;
}

const SESSION_DURATION = 6 * 60 * 60 * 1000; // 6 hours in milliseconds
const SESSION_KEY = 'admin_session';
const DEVICE_TOKEN_KEY = 'device_token';

export class AuthUtils {
  /**
   * Create a new session after successful login
   */
  static createSession(adminId: number, email: string, rememberDevice: boolean): void {
    const sessionData: SessionData = {
      admin_id: adminId,
      email: email,
      login_time: Date.now(),
      remember_device: rememberDevice
    };

    // Store session in localStorage (persists across tabs)
    localStorage.setItem(SESSION_KEY, JSON.stringify(sessionData));

    // If remember device is enabled, create a device token
    if (rememberDevice) {
      const deviceToken = this.generateDeviceToken();
      localStorage.setItem(DEVICE_TOKEN_KEY, deviceToken);
    } else {
      // Remove device token if not remembering
      localStorage.removeItem(DEVICE_TOKEN_KEY);
    }
  }

  /**
   * Check if current session is valid
   */
  static isSessionValid(): boolean {
    const sessionStr = localStorage.getItem(SESSION_KEY);
    
    if (!sessionStr) {
      return false;
    }

    try {
      const session: SessionData = JSON.parse(sessionStr);
      const currentTime = Date.now();
      const sessionAge = currentTime - session.login_time;

      // Check if session has expired (6 hours)
      if (sessionAge > SESSION_DURATION) {
        this.clearSession();
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error validating session:', error);
      return false;
    }
  }

  /**
   * Get current session data
   */
  static getSession(): SessionData | null {
    if (!this.isSessionValid()) {
      return null;
    }

    const sessionStr = localStorage.getItem(SESSION_KEY);
    if (!sessionStr) {
      return null;
    }

    try {
      return JSON.parse(sessionStr);
    } catch (error) {
      console.error('Error getting session:', error);
      return null;
    }
  }

  /**
   * Get admin ID from current session
   */
  static getAdminId(): number | null {
    const session = this.getSession();
    return session ? session.admin_id : null;
  }

  /**
   * Clear current session (logout)
   */
  static clearSession(): void {
    const session = this.getSession();
    
    // Only clear device token if "remember device" was not enabled
    if (session && !session.remember_device) {
      localStorage.removeItem(DEVICE_TOKEN_KEY);
    }
    
    localStorage.removeItem(SESSION_KEY);
  }

  /**
   * Check if device is remembered
   */
  static isDeviceRemembered(): boolean {
    const deviceToken = localStorage.getItem(DEVICE_TOKEN_KEY);
    return deviceToken !== null;
  }

  /**
   * Get device token
   */
  static getDeviceToken(): string | null {
    return localStorage.getItem(DEVICE_TOKEN_KEY);
  }

  /**
   * Generate a unique device token
   */
  private static generateDeviceToken(): string {
    return `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get remaining session time in milliseconds
   */
  static getRemainingSessionTime(): number {
    const session = this.getSession();
    if (!session) {
      return 0;
    }

    const currentTime = Date.now();
    const sessionAge = currentTime - session.login_time;
    const remaining = SESSION_DURATION - sessionAge;

    return remaining > 0 ? remaining : 0;
  }

  /**
   * Format remaining time as human readable string
   */
  static formatRemainingTime(): string {
    const remaining = this.getRemainingSessionTime();
    if (remaining <= 0) {
      return 'Session expired';
    }

    const hours = Math.floor(remaining / (60 * 60 * 1000));
    const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));

    if (hours > 0) {
      return `${hours}h ${minutes}m remaining`;
    }
    return `${minutes}m remaining`;
  }

  /**
   * Refresh session (extend the time)
   */
  static refreshSession(): boolean {
    const session = this.getSession();
    if (!session) {
      return false;
    }

    // Update login time to current time
    session.login_time = Date.now();
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    return true;
  }
}

export default AuthUtils;